import type { ObjectId } from '@freightview/object-id'

import {
  vArray,
  vBoolean,
  vCheck,
  vCountryAny,
  vCountryAnyToUpper,
  vEmail,
  vMaxLength,
  vNonEmptyString,
  vObject,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPhoneExt,
  vPhoneNumber,
  vPicklist,
  vPipe,
  vPostalCode,
  vStandardTime,
  vString,
} from '../validation/index.ts'
import type { ChargeName } from './charge.ts'
import type { Country, LegacyCountry } from './country.ts'
import { type CustomsBrokerType, customsBrokerTypes } from './customs-broker.ts'
import type { GeoPoint } from './geo-point.ts'
import {
  type FlatbedHandlingType,
  flatbedHandlingTypes,
  type LocationAccessorialType,
  locationAccessorialSchema,
} from './location-accessorial.ts'
import { type ShipType, vShipType } from './ship-type.ts'

export interface AddressBase {
  address: string
  address2?: string
  adminArea3?: string
  city: string
  state: string
  postalCode: string
  country: Country
}

export interface GeocodedAddressFields {
  lat: number
  lng: number
  timezone: string
  geo?: GeoPoint
}

export interface LegacyAddressBase extends Omit<AddressBase, 'country'> {
  country: LegacyCountry
}

export interface CompanyAddressBase extends AddressBase {
  company?: string
}

export const addressBaseSchema = vObject({
  address: vNonEmptyString(),
  address2: vOptional(vString()),
  adminArea3: vOptional(vString()),
  city: vNonEmptyString(),
  state: vNonEmptyString(),
  postalCode: vPostalCode(),
  country: vCountryAny,
})

export interface LocationAccessorial {
  key: LocationAccessorialType
}

const addressSources = ['dispatch', 'manual', 'import', 'vendor-sync'] as const
export const addressTypes = ['billing', 'shipping', 'customs-broker'] as const

export type AddressType = (typeof addressTypes)[number]

export interface CustomsBrokerData {
  type?: CustomsBrokerType
}

export interface AddressModel
  extends Omit<AddressBase, 'country'>,
    GeocodedAddressFields {
  _id: ObjectId
  accountId: ObjectId
  vendorId?: ObjectId
  locationId?: ObjectId // account location id
  created: Date
  accountLocation: boolean
  company: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
  contactPhoneExt?: string
  country: LegacyCountry
  bolFollowers: string[]
  charges: Array<{ name: ChargeName }>
  accessorials?: LocationAccessorial[]
  dockHoursStart?: string
  dockHoursEnd?: string
  flatbedHandlingType?: FlatbedHandlingType
  instructions: string
  internalNotes?: string
  sendBOL?: boolean
  shipType: ShipType
  source: (typeof addressSources)[number]
  type: AddressType
  customsBroker?: CustomsBrokerData
  lastUpdated?: Date
  lastUsed?: Date
}

export const mappedAccessorialsSchema = vObject({
  ltl: vArray(locationAccessorialSchema),
  parcel: vArray(locationAccessorialSchema),
  truckload: vArray(locationAccessorialSchema),
})

export type MappedAccessorials = vOutput<typeof mappedAccessorialsSchema>

export const customsBrokerDataSchema = vObject({
  type: vOptional(vPicklist(customsBrokerTypes)),
})
const isShippingAddress = ({ type }: { type?: AddressType }): boolean => {
  return !type || type === 'shipping'
}

export const addressUpsertSchema = vPipe(
  vObject({
    address: vString(),
    address2: vOptional(vString()),
    adminArea3: vOptional(vString()),
    city: vString(),
    state: vString(),
    postalCode: vPostalCode(true),
    country: vCountryAnyToUpper(),
    company: vNonEmptyString(),
    contactName: vOptional(vString()),
    contactEmail: vOptional(vEmail),
    contactPhone: vOptional(vPhoneNumber()),
    contactPhoneExt: vOptional(vPhoneExt()),
    dockHoursStart: vOptional(vStandardTime()),
    dockHoursEnd: vOptional(vStandardTime()),
    flatbedHandlingType: vOptional(vPicklist(flatbedHandlingTypes)),
    instructions: vOptional(vString()),
    internalNotes: vOptional(vPipe(vString(), vMaxLength(250))),
    bolFollowers: vOptional(vArray(vString())),
    sendBOL: vOptionalDef(vBoolean(), false),
    shipType: vShipType(),
    source: vOptional(vPicklist(addressSources)),
    type: vOptional(vPicklist(addressTypes)),
    mappedAccessorials: mappedAccessorialsSchema,
    customsBroker: vOptional(customsBrokerDataSchema),
  }),
  vCheck(
    data => !isShippingAddress(data) || !!data.address,
    'Address is required',
  ),
  vCheck(data => !isShippingAddress(data) || !!data.city, 'City is required'),
  vCheck(data => !isShippingAddress(data) || !!data.state, 'State is required'),
  vCheck(
    data => !isShippingAddress(data) || !!data.postalCode,
    'Postal code is required',
  ),
)

export type AddressUpsertDTO = vOutput<typeof addressUpsertSchema>
